import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { TMnemoSchemeItem } from '@web/entities/mnemonic/model/mnemonic.type'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'
import type { TId } from '@web/shared/types/id.type'

interface IStageOptions {
  width: number
  height: number
  scale: number
}

export interface IMnemonicInitialState {
  mnemoSchemeElement: TMnemoSchemeItem | null
  selectedMnemoElementIds: TId[]
  stageOptions: IStageOptions
}

const mnemonicInitialState: IMnemonicInitialState = {
  mnemoSchemeElement: null,
  selectedMnemoElementIds: [],
  stageOptions: { width: 1, height: 1, scale: 1 }
}

export const mnemonicSlice = createSlice({
  name: 'mnemonic',
  initialState: mnemonicInitialState,
  reducers: {
    setMnemoSchemeItem(state, action: PayloadAction<TMnemoSchemeItem>) {
      state.mnemoSchemeElement = action.payload
    },
    setSelectedMnemoElement(state, action: PayloadAction<TId | TId[]>) {
      if (Array.isArray(action.payload)) {
        state.selectedMnemoElementIds = [...action.payload]
      } else {
        state.selectedMnemoElementIds = [action.payload]
      }
    },
    toggleSelectedMnemoElements(state, action: PayloadAction<TId>) {
      const selectedMnemoElementId = action.payload
      const selectedMnemoElementIds = new Set(
        state.selectedMnemoElementIds.map((mnemoElementId) => mnemoElementId)
      )

      if (selectedMnemoElementIds.has(selectedMnemoElementId)) {
        state.selectedMnemoElementIds = state.selectedMnemoElementIds.filter(
          (id) => id !== selectedMnemoElementId
        )
      } else {
        state.selectedMnemoElementIds.push(selectedMnemoElementId)
      }
    },
    clearSelectedMnemoSchemeElements(state) {
      state.selectedMnemoElementIds = []
    },
    incrementScale(state) {
      state.stageOptions.scale = state.stageOptions.scale + 0.1
    },
    decrementScale(state) {
      state.stageOptions.scale =
        state.stageOptions.scale >= 0.2
          ? state.stageOptions.scale - 0.1
          : state.stageOptions.scale
    },
    setStageOptions(state, { payload }: PayloadAction<Partial<IStageOptions>>) {
      state.stageOptions = { ...state.stageOptions, ...payload }
    }
  }
})

export const mnemonicReducer = mnemonicSlice.reducer
export const useMnemoActions = () => useDispatchedActions(mnemonicSlice.actions)
